// TransactionAnalyzerPanel — center-panel wrapper that lets the chat agent
// render a filtered transaction set in the user's main workspace using the
// same TransactionAnalyzer the dashboard drilldowns and DRFA rows land on.
//
// Why this thin wrapper exists: the agent's `render_transaction_analysis`
// tool resolves a filter spec to a transaction-ID list server-side and pushes
// just the IDs through AppSync. This component receives the IDs, pipes them
// through useTransactionDrilldown (which fetches enriched rows in paged
// batches via the standard SWR-infinite path), and hands the result to
// TransactionAnalyzer. End result: the agent-rendered view is functionally
// identical to clicking a dashboard card or DRFA row — same filters, same
// sankey, same PDF print, same edit hooks.
//
// Props (from agent's render_transaction_analysis tool):
//   title: string — banner label for the rendered set
//   transaction_ids: string[] — the IDs the agent wants displayed
//   onInteraction: (event) => void — center-panel callback (provided by
//                  CenterPanelRenderer; we use it for "clear panel" so the
//                  user can dismiss the agent-rendered view)

import React, { useState } from 'react';
import { perspectiveMap } from '../ui/report-sankey-analysis.js';
import { useTransactionDrilldown } from '../../hooks/useTransactionDrilldown.js';
import TransactionAnalyzer from '../transactions/TransactionAnalyzer.js';

export default function TransactionAnalyzerPanel({ title, transaction_ids, onInteraction }) {
  const ids = Array.isArray(transaction_ids) ? transaction_ids : [];
  const { transactions, mutate, isLoading, totalCount, loadedCount } = useTransactionDrilldown({
    transactionIDs: ids,
    isActive: ids.length > 0,
  });

  // Sankey perspective is a UI-only setting here — no URL routing in the
  // center panel. Default to CATEGORY (the most common view).
  const [perspective, setPerspective] = useState('CATEGORY');
  const safePerspective = perspective in perspectiveMap ? perspective : 'CATEGORY';

  const drilldown = {
    label: title || 'Filtered Transactions',
    onClear: () => {
      // Tell the renderer to dismiss this panel. CenterPanelRenderer.onInteraction
      // → AppShell-level handler clears panelConfig (returns the user to the
      // default page content). If onInteraction is wired, we use the standard
      // close_panel event; otherwise no-op so the analyzer at least renders.
      if (typeof onInteraction === 'function') {
        onInteraction({ type: 'close_panel', source: 'TransactionAnalyzerPanel' });
      }
    },
  };

  // Key the analyzer by a stable hash of the ID set so a second
  // render_transaction_analysis call (e.g., agent narrowing 584 Uber → 5
  // flagged Uber) unmounts and remounts the subtree. This wipes all of the
  // analyzer's internal state — most importantly the filter panel's
  // selectedValues, which would otherwise carry over from the previous
  // (broader) data set and produce stale chips + the MUI Autocomplete
  // warning "None of the options match with [...]". Length + first + last
  // is a cheap hash that changes on any meaningful set change without
  // re-running on dataset reordering or React strict-mode double-renders.
  const idsKey = ids.length > 0
    ? `${ids.length}:${ids[0]}:${ids[ids.length - 1]}`
    : 'empty';

  return (
    <TransactionAnalyzer
      key={idsKey}
      transactions={transactions}
      isLoading={isLoading}
      mutate={mutate}
      perspective={safePerspective}
      onPerspectiveChange={setPerspective}
      drilldown={drilldown}
      loadedCount={loadedCount}
      totalCount={totalCount}
    />
  );
}
